import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  DevHubBox,
  DevHubTooltip,
  DevHubDynamicIcon,
  DevHubIconButton,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import { Workbox } from '../../home/Workbox';
import { IdeaCarousel } from './IdeaCarousel';
import { OnboardIdeaButton } from './OnboardIdeaButton';
import {
  ExperimentalApplication,
  IdeaFromAPIResponse,
  mapIdeaToExperimentalApplication,
} from './types';
import { UserContext } from '../../../contexts/UserContext';
import { discoveryApiRef, useApi } from '@backstage/core-plugin-api';
import { deletionStateManager } from './deletionStateManager';

const useStyles = makeStyles(() => ({
  scrollContainer: {
    overflowY: 'auto',
    height: '100%',
    maxHeight: '500px', // Set a max height to ensure scrolling triggers
    // Custom scrollbar styling
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderRadius: 3,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
      },
    },
  },
}));

/**
 * A component that displays ideas associated to a User.
 * @constructor
 */
export const MyIdeas = () => {
  const classes = useStyles();
  const [experimentalApps, setExperimentalApps] = useState<
    ExperimentalApplication[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [refreshingAppId, setRefreshingAppId] = useState<number | null>(null);
  const { login } = useContext(UserContext);
  const discoveryApi = useApi(discoveryApiRef);

  // Fetch experimental apps from backend
  const fetchExperimentalApps = async () => {
    if (!login) return;

    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('experimental-apps');
      const url = `${baseUrl}?userLogin=${login}`;
      // eslint-disable-next-line no-console
      console.log('MyIdeas: Fetching apps from:', url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch apps: ${response.statusText}`);
      }

      const ideas: IdeaFromAPIResponse = await response.json();
      // eslint-disable-next-line no-console
      console.log('MyIdeas: Received ideas:', ideas);

      // Map the new API format to ExperimentalApplication format
      const mappedApps = ideas.applications.map(
        mapIdeaToExperimentalApplication,
      );
      // eslint-disable-next-line no-console
      console.log('MyIdeas: Mapped apps:', mappedApps);

      setExperimentalApps(mappedApps);

      // Clean up stale deletion states (apps that were deleted outside this session)
      const appIds = mappedApps.map(app => app.applicationId);
      // eslint-disable-next-line no-console
      console.log('MyIdeas: Calling cleanupStaleStates with app IDs:', appIds);
      if ('cleanupStaleStates' in deletionStateManager) {
        (deletionStateManager as any).cleanupStaleStates(appIds);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching experimental apps:', error);
      setExperimentalApps([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch apps on mount
  useEffect(() => {
    fetchExperimentalApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  const handleRefresh = () => {
    fetchExperimentalApps();
  };

  const handleRefreshSingleIdea = async (applicationId: number) => {
    if (!login) return;

    setRefreshingAppId(applicationId);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('experimental-apps');
      const url = `${baseUrl}?userLogin=${login}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch apps: ${response.statusText}`);
      }

      const ideas: IdeaFromAPIResponse = await response.json();

      // Map all apps
      const mappedApps = ideas.applications.map(
        mapIdeaToExperimentalApplication,
      );

      // Find the specific app and update only that one in state
      const updatedApp = mappedApps.find(
        app => app.applicationId === applicationId,
      );

      if (updatedApp) {
        setExperimentalApps(prev =>
          prev.map(app => {
            if (app.applicationId === applicationId) {
              // Preserve original timestamps to maintain sort order
              return {
                ...updatedApp,
                createdAt: app.createdAt,
                updatedAt: new Date().toISOString(),
              };
            }
            return app;
          }),
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error refreshing single app:', error);
    } finally {
      setRefreshingAppId(null);
    }
  };

  const handleDeleteIdea = () => {
    // Refresh the list after deletion
    fetchExperimentalApps();
  };

  const actionButton = (
    <DevHubBox
      display="flex"
      alignItems="center"
      style={{
        gap: '8px',
        marginTop: '16px',
        marginRight: '16px',
      }}
    >
      <DevHubTooltip title="Refresh ideas">
        <DevHubIconButton
          size="small"
          onClick={handleRefresh}
          disabled={loading}
        >
          <DevHubDynamicIcon
            color="pink"
            iconName="Refresh"
            fontSize="medium"
          />
        </DevHubIconButton>
      </DevHubTooltip>
      <OnboardIdeaButton onSuccess={handleRefresh} />
    </DevHubBox>
  );

  return (
    <>
      <Workbox
        consistentHeight
        color="purple"
        title="My Ideas"
        component="ideas"
        buttonTitle="View All"
        showButton
        navigatePath="/action-center/my-ideas"
        actionItem={actionButton}
        info="This section will display your experimentation ideas and their status"
        subHeader="Access your experimental applications"
      >
        <div className={classes.scrollContainer}>
          {loading && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <Progress />
              {experimentalApps.length > 0 && (
                <IdeaCarousel
                  ideas={experimentalApps}
                  onRefresh={handleRefreshSingleIdea}
                  onDelete={handleDeleteIdea}
                  refreshingAppId={refreshingAppId}
                />
              )}
            </div>
          )}
          {!loading && experimentalApps.length > 0 && (
            <IdeaCarousel
              ideas={experimentalApps}
              onRefresh={handleRefreshSingleIdea}
              onDelete={handleDeleteIdea}
              refreshingAppId={refreshingAppId}
            />
          )}
          {!loading && experimentalApps.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              No ideas onboarded yet. Click "Onboard an Idea" to get started!
            </div>
          )}
        </div>
      </Workbox>
    </>
  );
};
